import { useEffect, useState } from 'react'
import { company, CustomerApplication } from '../../utilities/CustomerData'
import { useParams } from 'react-router-dom'
import { apiURL } from '../../utilities/URLs'
import { ApplicationStep1 } from './ApplicationSteps/ApplicationStep1'
import { ApplicationStep2 } from './ApplicationSteps/ApplicationStep2'
import posthog from 'posthog-js'
import TagManager from 'react-gtm-module'
import { ApplicationStep3 } from './ApplicationSteps/ApplicationStep3'
import { ApplicationStep4 } from './ApplicationSteps/ApplicationStep4'
import { ApplicationStep5 } from './ApplicationSteps/ApplicationStep5'
import { ApplicationStep6 } from './ApplicationSteps/ApplicationStep6'
import { ApplicationStep7 } from './ApplicationSteps/ApplicationStep7'
import { ApplicationStep8 } from './ApplicationSteps/ApplicationStep8'
import { getCompanyGTMID, getDefaultCompanyId, getGTMDataLayerBody } from '../../utilities/extensions'
import { ApplicationPreQuali } from './ApplicationSteps/ApplicationPreQuali'
import { ApplicationNetIncome } from './ApplicationSteps/ApplicationNetIncome'
import { ApplicationNotQualified } from './ApplicationSteps/ApplicationNotQualified'
import { useTwitterPixel, trackTwitterConversion } from '../../utilities/TwitterPixel'
import ReactPixel from 'react-facebook-pixel'
import { CannellaMedia } from '../../utilities/CannellaMedia/CannellaMedia'

import poweredBy from '../../assets/ApplicationIcons/poweredBy.png'
import { CannellaMediaEvent } from '../../utilities/CannellaMedia/CannellaMediaEvent'

export default function NewApplication() {
  const { leadSource } = useParams()
  const searchParams = new URLSearchParams(window.location.search)

  const queryParams = {}
  for (const [key, value] of searchParams.entries()) {
    queryParams[key] = value
  }

  const companyGTM = getCompanyGTMID(company.companyId)
  if(companyGTM) {
    TagManager.initialize({
      gtmId: companyGTM
    })
  }

  useTwitterPixel(leadSource)

  const totalSteps = 8

  const [customerApplication, setCustomerApplication] = useState(new CustomerApplication())
  const [step, setStep] = useState(0)
  const [showPreQualifyQuestions, setShowPreQualifyQuestions] = useState(false)
  const [preQualifyStep, setPreQualifyStep] = useState(0)
  const [finishedApplication, setFinishedApplication] = useState(false)
  const [showApplicationError, setShowApplicationError] = useState(false)
  const [showDuplicateCustomerError, setShowDuplicateCustomerError] = useState(false)

  const [taxCreditGovJanuary, setTaxCreditGovJanuary] = useState([])
  const [taxCreditGovApril, setTaxCreditGovApril] = useState([])
  const [taxCreditChildJanuary, setTaxCreditChildJanuary] = useState([])
  const [taxCreditChildApril, setTaxCreditChildApril] = useState([])
  const [taxCreditFamilyJanuary, setTaxCreditFamilyJanuary] = useState(0)
  const [taxCreditFamilyApril, setTaxCreditFamilyApril] = useState(0)
  const [estimateRefund, setEstimateRefund] = useState(0)

  const [createApplication, setCreateApplication] = useState(false)
  const [phoneInUse, setPhoneInUse] = useState(false)
  const [emailInUse, setEmailInUse] = useState(false)

  const handleApplicationStep1 = (firstName, lastName, email, phone, referralCode) => {
    setCustomerApplication((prev) => ({
      ...prev,
      firstName: firstName,
      lastName: lastName,
      email: email,
      phone: '+' + phone
    }))
    if(companyGTM)
      TagManager.dataLayer(getGTMDataLayerBody(company.companyId, queryParams, 'conversion'))

    setCreateApplication(true)
  }

  const handlePreQualiQuestions = (isSelfEmployed, isSmallOwner, hadFamilyMember) => {
    setCustomerApplication((prevApplication) => ({
      ...prevApplication,
      isSelfEmployed: isSelfEmployed,
      isSmallOwner: isSmallOwner,
      hadFamilyMember: hadFamilyMember
    }))

    if((isSelfEmployed === 'Yes' || isSmallOwner === 'Yes') && hadFamilyMember === 'Yes') {
      setPreQualifyStep(1)
      if(companyGTM)
        TagManager.dataLayer(getGTMDataLayerBody(company.companyId, queryParams, 'prequalification'))


    } else {
      setPreQualifyStep(2)
      handleSubmitApplication('Not Qualified')
      if(window.afGTag && company.companyId === '6243c824-9e29-46da-94c6-96d03d8cbb84') {
        window.afGTag('event', 'conversion', {
          'send_to': 'AW-16699126403/ixj3CK2WxNIZEIPl4Zo-',
          'value': 1.0,
          'currency': 'USD'
        })
      }
    }
  }

  const handleApplicationNetIncome = (netIncome) => {
    setCustomerApplication((prevApplication) => ({
      ...prevApplication,
      netIncome: netIncome
    }))

    setStep(step+1)
  }

  const handleApplicationStep2 = (noTaxCreditGovJanuary, taxCreditGovJanuary) => {
    setCustomerApplication((prev) => ({
      ...prev,
      noTaxCreditGovJanuary: noTaxCreditGovJanuary,
      taxCreditGovJanuary: taxCreditGovJanuary,
      executedConversion: true
    }))

    setStep(step + 1)
  }

  const handleBackToNetIncome = () => {
    setStep(step-1)
    setPreQualifyStep(1)
  }

  const handleSetApplicationStep2 = (taxCreditGovJanuary) => {
    setTaxCreditGovJanuary(taxCreditGovJanuary)
    setCustomerApplication((prevApplication) => ({
      ...prevApplication,
      taxCreditGovJanuary: taxCreditGovJanuary
    }))
  }

  const handleApplicationStep3 = (noTaxCreditGovApril, taxCreditGovApril) => {
    setCustomerApplication((prev) => ({
      ...prev,
      noTaxCreditGovApril: noTaxCreditGovApril,
      taxCreditGovApril: taxCreditGovApril,
    }))

    if(taxCreditGovJanuary.length < 10)
      setStep(step+1)
    else if(taxCreditGovApril.length < 10)
      setStep(step+2)
    else
      setStep(step+3)
  }

  const handleSetApplicationStep3 = (taxCreditGovApril) => {
    setTaxCreditGovApril(taxCreditGovApril)
    setCustomerApplication((prevApplication) => ({
      ...prevApplication,
      taxCreditGovApril: taxCreditGovApril
    }))
  }

  const handleApplicationStep4 = (noTaxCreditChildJanuary, taxCreditChildJanuary) => {
    setCustomerApplication((prev) => ({
      ...prev,
      noTaxCreditChildJanuary: noTaxCreditChildJanuary,
      taxCreditChildJanuary: taxCreditChildJanuary,
    }))

    if(taxCreditGovApril.length < 10)
      setStep(step+1)
    else
      setStep(step+2)
  }

  const handleSetApplicationStep4 = (taxCreditChildJanuary) => {
    setTaxCreditChildJanuary(taxCreditChildJanuary)

    setCustomerApplication((prevApplication) => ({
      ...prevApplication,
      taxCreditChildJanuary: taxCreditChildJanuary
    }))
  }

  const handleApplicationStep5 = (noTaxCreditChildApril, taxCreditChildApril) => {
    setCustomerApplication((prev) => ({
      ...prev,
      noTaxCreditChildApril: noTaxCreditChildApril,
      taxCreditChildApril: taxCreditChildApril,
    }))

    setStep(step + 1)
  }

  const handleSetApplicationStep5 = (taxCreditChildApril) => {
    setTaxCreditChildApril(taxCreditChildApril)

    setCustomerApplication((prevApplication) => ({
      ...prevApplication,
      taxCreditChildApril: taxCreditChildApril
    }))
  }

  const handleApplicationStep6 = (childUnder18, taxCreditFamilyJanuary, taxCreditFamilyApril) => {
    setCustomerApplication((prev) => ({
      ...prev,
      childUnder18: childUnder18,
      taxCreditFamilyJanuary: taxCreditFamilyJanuary,
      taxCreditFamilyApril: taxCreditFamilyApril
    }))

    setStep(step + 1)
    if(childUnder18 === 'No')
      handleSetApplicationStep6(0,0)
  }

  const handleSetApplicationStep6 = (taxCreditFamilyJanuary, taxCreditFamilyApril) => {
    setTaxCreditFamilyJanuary(taxCreditFamilyJanuary)
    setTaxCreditFamilyApril(taxCreditFamilyApril)

    setCustomerApplication((prev) => ({
      ...prev,
      taxCreditFamilyJanuary: taxCreditFamilyJanuary,
      taxCreditFamilyApril: taxCreditFamilyApril
    }))
  }

  const handleApplicationStep7 = (last4SSN, birthYear) => {
    setCustomerApplication((prev) => ({
      ...prev,
      last4SSN: last4SSN,
      birthYear: birthYear
    }))

    setStep(step + 1)
  }

  const handleCreateApplication = async () => {
    setCreateApplication(true)
    customerApplication.stage = 'Application Started'
    customerApplication.source = leadSource ? leadSource : 'Website Home'
    customerApplication.companyId = queryParams.companyId ?? getDefaultCompanyId(window.location.hostname)
    customerApplication.creatingApplication = true

    const formData = new FormData()
    formData.append('customerApplication', JSON.stringify(customerApplication))
    const customerApplicationResponse = await fetch(`${apiURL}/api/createCustomerApplication`, {
      method: 'POST',
      body: formData
    })
    const customerApplicationData = await customerApplicationResponse.json()
    setCreateApplication(false)
    if (customerApplicationData.response.error) {
      console.error(customerApplicationData.response.error)
      if (customerApplicationData.response.error.startsWith("Customer with name and SSN found")) {
        setShowDuplicateCustomerError(true)
      } else if(customerApplicationData.response.error === 'Contact Information In Use.') {
        setEmailInUse(customerApplicationData.response.emailInUse)
        setPhoneInUse(customerApplicationData.response.phoneInUse)
      } else
        setShowApplicationError(true)
    } else {
      setCustomerApplication((prevApplication) => ({
        ...prevApplication,
        leadId: customerApplicationData.response.leadId,
        contactId: customerApplicationData.response.customerId
      }))

      if((leadSource === 'Bill Hillestad' || leadSource === 'RIF Social, LLC') && window.location.hostname === 'myportal.setcpros.com')
        ReactPixel.trackCustom('Questionnaire Started')

      setShowPreQualifyQuestions(true)
    }
  }

  const handleSubmitApplication = async (stageName) => {
    customerApplication.stage = stageName
    customerApplication.source = leadSource ? leadSource : 'Website Home'
    customerApplication.companyId = queryParams.companyId ?? getDefaultCompanyId(window.location.hostname)
    customerApplication.gclid = queryParams.gclid ?? ''
    customerApplication.fclid = queryParams.fclid ?? ''
    customerApplication.clickid = queryParams.clickid ?? ''
    customerApplication.sub1 = queryParams.sub1 ?? ''
    customerApplication.sub2 = queryParams.sub2 ?? ''
    customerApplication.sub3 = queryParams.sub3 ?? ''
    customerApplication.sub4 = queryParams.sub4 ?? ''
    customerApplication.sub5 = queryParams.sub5 ?? ''
    customerApplication.utm_source = queryParams.utm_source ?? ''
    customerApplication.utm_medium = queryParams.utm_medium ?? ''
    customerApplication.utm_campaign = queryParams.utm_campaign ?? ''
    customerApplication.estimateRefund = estimateRefund
    customerApplication.creatingApplication = false

    if(leadSource === 'Brian Kiplinger' && window.location.hostname === 'myportal.setcpros.com')
      trackTwitterConversion('tw-p72x9-p7s0w', Math.floor(Math.random() * 1000000).toString(), customerApplication.email, customerApplication.phone)

    const formData = new FormData()
    formData.append('customerApplication', JSON.stringify(customerApplication))

    const customerApplicationResponse = await fetch(`${apiURL}/api/createCustomerApplication`, {
      method: 'POST',
      body: formData
    })
    const customerApplicationData = await customerApplicationResponse.json()
    if (customerApplicationData.response.error) {
      console.error(customerApplicationData.response.error)
      if (customerApplicationData.response.error.startsWith("Customer with name and SSN found"))
        setShowDuplicateCustomerError(true)

      setShowApplicationError(true)
    } else if (customerApplicationData.response.accessToken) {
      if(stageName === 'Pre-Qualified-N') {
        setFinishedApplication(true)

        if(queryParams.sub5 && window.location.hostname === 'pros.setctaxreturn.com')
          await fetch(`https://www.rndgb8trk.com/?nid=3128&transaction_id=${queryParams.sub5}`)

        if(queryParams.sub4 && queryParams.sub5 && window.location.hostname === 'pros.setctaxreturn.com')
          await fetch(`https://rigidtrck.com/p.ashx?o=417&e=40&f=pb&r=${queryParams.sub4}&t=${queryParams.sub5}`)

        if(companyGTM)
          TagManager.dataLayer(getGTMDataLayerBody(company.companyId, queryParams, 'application'))

        if((leadSource === 'Bill Hillestad' || leadSource === 'RIF Social, LLC') && window.location.hostname === 'myportal.setcpros.com')
          ReactPixel.trackCustom('Lead')

        posthog.identify(customerApplicationData.response.contactId, {
          name: customerApplication.firstName+' '+customerApplication.lastName,
          email: customerApplication.email
        })
        window.top.location.href = `${apiURL}/preQualified/${customerApplicationData.response.accessToken}/${customerApplicationData.response.contactId}`
      }
    }
  }

  useEffect(() => {
    const handleCalculateEstimateRefund = async () => {
      const form = new FormData()
      form.append('refundInfo', JSON.stringify({
        netIncome: customerApplication.netIncome,
        taxCreditGovJanuary: taxCreditGovJanuary,
        taxCreditGovApril: taxCreditGovApril,
        taxCreditChildJanuary: taxCreditChildJanuary,
        taxCreditChildApril: taxCreditChildApril,
        taxCreditFamilyJanuary: taxCreditFamilyJanuary,
        taxCreditFamilyApril: taxCreditFamilyApril
      }))
  
      const refundResponse = await fetch(`${apiURL}/api/calculateEstimateRefund`, {
        method: 'POST',
        body: form
      })
      const refundData = await refundResponse.json()
      if(refundData.response.error)
        setEstimateRefund(0)
      else
        setEstimateRefund(Number(refundData.response.estimateRefund))
    }

    handleCalculateEstimateRefund()
  }, [taxCreditGovJanuary, taxCreditGovApril, taxCreditChildJanuary, taxCreditChildApril, taxCreditFamilyJanuary, taxCreditFamilyApril])

  useEffect(() => {
    if(createApplication)
      handleCreateApplication()
  }, [createApplication])

  useEffect(() => {
    if((leadSource === 'Bill Hillestad' || leadSource === 'RIF Social, LLC') && window.location.hostname === 'myportal.setcpros.com') {
      ReactPixel.init('1582173432356356')
      ReactPixel.pageView()
    }

    posthog.startSessionRecording()
    return () => {
      posthog.stopSessionRecording()
    }
  }, [])

  return (
    <div className='relative w-screen h-screen flex flex-col items-center font-mont'>
      <div className='w-full mt-16 flex flex-col items-center overflow-x-hidden'>
        {!finishedApplication ? (
          !showApplicationError ? (
            <div className='w-full flex flex-col items-center'>
              {company.companyId === '11565d9b-2028-4b8e-86ae-0eaa939ddf61' && (
                <div className='max-sm:w-11/12 flex flex-col items-center gap-4 bg-secondaryColor p-6 rounded-xl shadow-md text-center'>
                  <img className='w-40 md:w-56 object-contain' src={company.companyLogoURL} alt='Company Logo' />
                  <p className='text-lg font-semibold text-white'>
                    Self-Employed Tax Credit | FFCRA | ARPA
                  </p>
                  <p className='text-xl font-bold text-blue-600'>
                    Discover if you qualify for up to <span className='text-green-500'>$32,220</span>
                  </p>
                </div>
              )}
              <div className='w-11/12 flex flex-row'>
                {Array.from({ length: totalSteps }, (_, index) => (
                  <div key={index} className={`h-2 w-full mx-2 rounded-full ${index <= step ? 'bg-primaryColor' : 'bg-gray-300'}`} />
                ))}
              </div>
              {step === 0 ? (
                !showPreQualifyQuestions ? (
                  <ApplicationStep1
                    _customerApplication={customerApplication}
                    createApplication={createApplication}
                    phoneInUse={phoneInUse}
                    emailInUse={emailInUse}
                    handleApplicationStep1={handleApplicationStep1}
                  />
                ) : (
                  preQualifyStep === 0 ? (
                    <ApplicationPreQuali
                      _customerApplication={customerApplication}
                      handlePreQualiQuestions={handlePreQualiQuestions} 
                    />
                  ) : preQualifyStep === 1 ? (
                    <ApplicationNetIncome
                      _customerApplication={customerApplication}
                      handleApplicationNetIncome={handleApplicationNetIncome}
                    />
                  ) : (
                    <ApplicationNotQualified />
                  )
                )
              ) : step === 1 ? (
                <ApplicationStep2
                  _customerApplication={customerApplication}
                  estimateRefund={estimateRefund}
                  handleBackToNetIncome={handleBackToNetIncome}
                  handleApplicationStep2={handleApplicationStep2}
                  handleSetApplicationStep2={handleSetApplicationStep2}
                />
              ) : step === 2 ? (
                <ApplicationStep3
                  _customerApplication={customerApplication}
                  estimateRefund={estimateRefund}
                  step={step}
                  setStep={setStep}
                  handleApplicationStep3={handleApplicationStep3}
                  handleSetApplicationStep3={handleSetApplicationStep3}
                />
              ) : step === 3 ? (
                <ApplicationStep4
                  _customerApplication={customerApplication}
                  estimateRefund={estimateRefund}
                  step={step}
                  setStep={setStep}
                  handleApplicationStep4={handleApplicationStep4}
                  handleSetApplicationStep4={handleSetApplicationStep4}
                />
              ) : step === 4 ? (
                <ApplicationStep5
                  _customerApplication={customerApplication}
                  estimateRefund={estimateRefund}
                  step={step}
                  setStep={setStep}
                  handleApplicationStep5={handleApplicationStep5}
                  handleSetApplicationStep5={handleSetApplicationStep5}
                />
              ) : step == 5 ? (
                <ApplicationStep6
                  _customerApplication={customerApplication}
                  estimateRefund={estimateRefund}
                  step={step}
                  setStep={setStep}
                  handleSetApplicationStep6={handleSetApplicationStep6}
                  handleApplicationStep6={handleApplicationStep6}
                />
              ) : step === 6 ? (
                <ApplicationStep7
                  _customerApplication={customerApplication}
                  estimateRefund={estimateRefund}
                  step={step}
                  setStep={setStep}
                  handleApplicationStep7={handleApplicationStep7}
                />
              ) : (
                <ApplicationStep8
                  step={step}
                  estimateRefund={estimateRefund}
                  setStep={setStep}
                  handleSubmitApplication={handleSubmitApplication}
                />
              )}
            </div>
          ) : (
            showDuplicateCustomerError ? (
              <div className='w-full flex flex-col items-center space-y-4'>
                <p className='font-bold text-4xl text-center text-red-600'>Oops!</p>
                <p className='text-center text-lg text-gray-800'>
                It looks like you already have an account with us! Please log in using your email address, or reset your password if you need a reminder. We’re here to help if you have any questions.
                </p>
                <p className='text-center text-lg text-gray-800'>
                  To restart the application, please just refresh the page.
                </p>
              </div>
            ) : (
              <div className='w-full flex flex-col items-center space-y-4'>
                <p className='font-bold text-4xl text-center text-red-600'>Oops!</p>
                <p className='text-center text-lg text-gray-800'>
                  There seems to have been a problem submitting your application. Please contact customer support at (833) 738-2776 to get further assistance in helping you submit your application!
                </p>
                <p className='text-center text-lg text-gray-800'>
                  To restart the application, please just refresh the page.
                </p>
              </div>
            )
          )
        ) : (
          <div className='w-full flex flex-col items-center space-y-4'>
            <p className='font-bold text-4xl text-center text-green-600'>Almost Done...</p>
            <p className='text-center text-lg text-gray-800'>
              You have successfully completed the first step of the application! The next step is to sync your IRS documents.
            </p>
            <p className='text-center text-gray-700'>
              You should have been redirected to the IRS documents page. If not, please check your email for a link to your customer portal to link your IRS documents from there.
            </p>
            
          </div>
        )}
      </div>
      {queryParams.companyId && queryParams.companyId !== '6243c824-9e29-46da-94c6-96d03d8cbb84' && (
        <img className='w-40 mt-auto mb-5 mr-auto ml-5' src={poweredBy} />
      )}
      
    </div>
  )
}

/**
 * <CannellaMedia leadSource={leadSource} />
 * <CannellaMediaEvent leadSource={leadSource} />
 */